<script lang="ts" setup>
	import type { FAQContext } from '~/components/main/faq.vue'
	import type { BenefitHighlightContext } from '~/components/molecules/benefit-highlight.vue'
	import type { ProcessesStepContext } from '~/components/molecules/process-steps.vue'
	import { useMediaQuery } from '@vueuse/core'


	definePageMeta({
		layout: 'default'
	})
	const isDialogOpen = shallowRef(false)
	const nuxtApp = useNuxtApp()
	const { sourceChannel } = useUtils()
	const seo = {
		title: "MyCoverGenius | Nigeria's #1 Insurance Services Provider",
		description:
			'MyCoverGenius is the #1 provider of Insurance & Insurance products in Nigeria including Life insurance, Auto insurance, Third-party insurance & health insurance for businesses.',
		tags: [
			'Life insurance',
			'Auto insurance',
			'third-party insurance',
			'health insurance',
			'insurance',
			'insurance for businesses',
			'insurance for individuals',
			'insurance for MSMEs',
			'insurance for startups',
			'insurance for corporates',
			'insurance for Nigeria',
			'insurance for Africa'
		],
		url: import.meta.env.VITE_BUSINESS_URL
	}

	useSeoMeta({
		title: seo.title,
		description: seo.description,
		ogTitle: seo.title,
		ogDescription: seo.description,
		ogType: 'website',
		ogLocale: 'en_NG',
		ogImage: 'https://res.cloudinary.com/dzhhy3ovo/image/upload/v1727082972/health-coverage_gpptol.png',
		ogImageAlt: 'MyCoverGenius - Insurance for smart businesses',
		twitterCard: 'summary_large_image',
		twitterSite: '@mycovergenius'
	})
	const faqList = ref<FAQContext[]>([
		{
			question: 'Why do I need insurance?',
			answer: `Life is unpredictable, and you may never see some things coming. You need insurance to protect yourself financially from unexpected events or losses. <br /> Insurance helps you manage risks by providing a safety net for situations like accidents, illnesses, property damage, or even death. It ensures that you or your loved ones won't face significant financial hardship in these situations, covering costs like medical bills, car or gadget repairs. In short, insurance gives you peace of mind.`,
			open: true
		},
		{
			question: 'How many insurance products does MyCoverGenius provide?',
			answer: `We offer a variety of insurance products, including health, auto, travel, and gadget insurance. Our offerings are tailored for both individuals and businesses, with flexible and affordable plans to meet various needs.`,
			open: false
		},
		{
			question: 'How do I buy insurance from MyCoverGenius?',
			answer: `<ol class='list  space-y-4  list-inside'>
			<li class='list-decimal'>Visit our website and choose from our range of insurance products, such as health, auto, travel, or gadget insurance, and select the one that suits your needs.</li>
			<li class='list-decimal'> Sign up for a business or individual account by providing the necessary details and creating login credentials.</li>
			<li class='list-decimal'> Make a purchase: Once you select the plan you need, you can complete the purchase using the available payment options.</li>
			<li class='list-decimal'> Your account will become active within 24 hours, and you can manage your plan(s) through your account dashboard.</li>
			</ol>`,
			open: false
		},
		{
			question: 'Can I add family and friend under my insurance plan?',
			answer: 'Yes, you can cover multiple family members under a single plan.',
			open: false
		}
	])

	const onboardingProcesses = shallowRef<ProcessesStepContext>([
		{
			icon: {
				dir: 'random',
				icon: 'wishlist',
				width: '60',
				height: '30'
			},
			title: 'Choose your insurance product',
			description:
				'Our lineup of options makes it easy for you to curate the perfect insurance combo for your business.'
		},
		{
			icon: {
				dir: 'random',
				icon: 'business-card',
				height: 100,
				width: 100
			},
			title: 'Sign up for a business account',
			description:
				'Sign up for a business account on the platform by providing your company details and creating login credentials.'
		},
		{
			icon: {
				dir: 'random',
				icon: 'umbrella',
				height: 80,
				width: 80
			},
			title: 'Get insured immediately',
			description:
				"Once your business account is set up and you've selected your desired insurance product, you can proceed to purchase coverage"
		}
	])
	const highlightedBenefits = shallowRef<BenefitHighlightContext>([
		{
			title: 'Everything in one spot',
			description:
				'Your one-stop shop for everything insurance - from staff health to gadgets, vehicles and even travel!',
			image: '/v1727739147/MSME-2_bxqo7c.webp',
			open: true
		},
		{
			title: 'Dedicated account manager',
			description: 'Get personalised support from your dedicated account manager',
			image: '/v1727082968/highlight-1_sfau66.webp'
		},
		{
			title: 'Fast digital claims',
			description: 'Get your claims settled quickly, making it easy for your employees and business.',
			image: '/v1727280053/claim-tracker_ma6ez6.webp'
		},
		{
			title: '24/7 hospital support',
			description:
				"Whether it's an emergency or just a quick question about your plan, count on our friendly support team to assist you.",
			image: '/v1727082953/highlight-3_v1z9gy.webp'
		},
		{
			title: 'Onboard in Minutes',
			description: 'Get your team onboarded in minutes, with zero paperwork.',
			image: '/v1727082967/highlight-4_rr0xhn.webp'
		},
		{
			title: 'Employee Mobile App',
			description:
				'You support your team, we make it easier. Give them app access so they can manage their insurance, stress-free.',
			image: '/v1761840291/mobile-community_zeztrj.webp'
		}
	])
	const profiles = ref([
		{
			src: '/v1727739485/MSMEs_q3ynf2.webp',
			name: 'Insurance for MSMEs',
			active: false
		},
		{
			src: '/v1727453189/large-corporates_apxka6.webp',
			name: 'Insurance for Large Corporates',
			active: true
		},
		{
			src: '/v1727082909/Startups_dp9gg6.webp',
			name: 'Insurance for Startups',
			active: false
		}
	])
	const isMobile = useMediaQuery('(max-width: 480px)')



	const onRegister = () => {
		resolve(() => {
			useMixpanel()?.track('web_onboarding', { product: 'home page' })
		})
		navigateTo(
			{ path: `${import.meta.env.VITE_BUSINESS_URL}/register`, query: sourceChannel.value },
			{ external: true, open: { target: '_blank' } }
		)
	}

	onBeforeMount(() => {
		if (nuxtApp.isHydrating) {
			const bannerDisplayDate = localStorage.getItem('update-banner')
			if (bannerDisplayDate) {
				localStorage.removeItem('update-banner')
			}
		}
	})
</script>

<template>
	<div class="overflow-x-hidden">
		<header class="md:min-h-[700px] relative mt-0 bg-base-800 dark:bg-base-900 px-5 pt-12 xs:pt-16">
			<ui-h1
				title="Insurance <br class='sm:hidden' /> re-imagined for"
				:animate-title="['smart businesses', 'startups', 'SMEs']"
				paragraph="We get it - the current way of doing insurance bores you, it bores us too! Let's get you onboard with the
			easy way of protecting your business, staff, and assets."
				:ui="{
					paragraphClass: { text: 'md:text-xl ' },
					btn: {
						primary: {
							text: { base: 'text-base-800 max-sm:text-sm group-hover:text-white' },
							btn: { width: 'md:mt-10', onHoverPseudo: 'hover:before:bg-base-primary ' }
						},
						secondary: { base: 'max-sm:text-sm' }
					}
				}"
				:primary-btn-text="`Onboard your business in <ClientOnly fallback='5'>${isMobile ? '2' : '5'}</ClientOnly> minutes <span class='hidden md:inline'>or less</span>`"
				h-class="text-[2.75rem]/[3.135rem] -tracking-[0.0825rem] md:text-[3.625rem]/[3.83rem] md:-tracking-[0.10rem] lg:text-[4.25rem]/[4.5rem] lg:-tracking-[0.135rem] max-w-[840px]"
				secondary-btn-text="Speak to us to get on board"
				class="md:gap-y-8 relative z-10"
				@on-primary="() => onRegister()"
			>
				<template #top>
					<div
						class="hidden items-center mb-5 gap-x-2 p-[8px_8px] xs:p-[8px_18px] sm:p-[12px_24px] bg-[#3b665e] rounded-lg"
					>
						<tools-icon icon="announce" dir="random" svg-class="sm:h-[24px] h-[22px]" />
						<div class="text-white text-xs xs:text-sm leading-normal text-balance md:text-pretty">
							<span class="font-bold sm:inline hidden">We heard you! </span>
							<span class="font-semibold"> Curious about the new experience waiting for you? 🎉 </span>
							<nuxt-link
								to="/guide"
								class="md:pl-2 underline items-center font-[700] inline-flex gap-x-3"
							>
								Click here to find out more
								<tools-icon dir="arrows" icon="arrow-short-r" />
							</nuxt-link>
						</div>
					</div>
				</template>
			</ui-h1>

			<molecules-user-profiles
				class="max-w-[1000px] mx-auto md:absolute md:-bottom-52 pt-8 left-0 right-0"
				:profiles="profiles"
			/>
		</header>
		<ui-spacer />

		<ui-pad class="mx-auto">
			<LazyMoleculesPartners />
			<ui-spacer />
			<LazyMainBenefitDescription
				title="Not your regular. We provide insurance that works."
				large-on-mobile
				tag="Why MyCoverGenius"
				:highlighted-benefits="highlightedBenefits"
			/>
		</ui-pad>
		<ui-spacer />

		<LazyMainProductPricing />
		<ui-spacer />

		<ui-pad class="mx-auto xs:py-0 py-0">
			<LazyMoleculesProcessSteps
				tag="Get Covered"
				:processes="onboardingProcesses"
				@trigger="() => onRegister()"
			/>
		</ui-pad>
		<ui-spacer />
		<LazyMainDigitalProcess class="w-full" @trigger="() => onRegister()" />
		<ui-spacer />
		<ui-pad class="xs:py-0 py-0">
			<LazyMoleculesTestimonials />
		</ui-pad>
		<ui-spacer />
		<LazyMoleculesDemoCard @secondary-click="onRegister" />
		<ui-spacer />
		<ui-pad :ui="{ paddingBlock: '' }" class="mx-auto xs:py-0 py-0">
			<LazyMoleculesInvestors class="pt-12 pb-20" />
			<LazyMainFaq :faq-list="faqList" />
		</ui-pad>

		<ui-spacer />

		<lazy-molecules-auth-dialog v-model="isDialogOpen" selected="register" />
	</div>
</template>
